import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, FileText, Loader2, ShieldCheck } from 'lucide-react';
import { EightDReport } from '../../types/eightd.types';
import { generate8DReportPDF } from '../../utils/exportUtils';
import { ESignatureModal, ESignatureData } from '../shared/ESignatureModal';

interface EightDHeaderProps {
    id?: string;
    report: Partial<EightDReport>;
    saving: boolean;
    handleSave: (signatureData?: ESignatureData) => void;
}

export const EightDHeader: React.FC<EightDHeaderProps> = ({
    id,
    report,
    saving,
    handleSave,
}) => {
    const [exporting, setExporting] = useState(false);
    const [showSignModal, setShowSignModal] = useState(false);

    const handleSign = (signatureData: ESignatureData) => {
        handleSave(signatureData);
    };

    const handleExportPDF = async () => {
        if (!report) return;

        setExporting(true);
        try {
            await generate8DReportPDF({
                eightDNumber: report.eightDNumber || id || 'DRAFT',
                title: report.title || 'Untitled 8D Report',
                status: report.status || 'Draft',
                createdAt: report.createdAt || new Date(),
                teamLead: report.d0_formation?.teamLeaderName,
                d0_problem: report.d0_formation?.problemDescription,
                d1_team: report.d0_formation?.teamMembers?.map(m => m.name).join(', '),
                d2_description: report.d1_problem?.problemStatement,
                d3_containment: report.d2_containment?.map(a => ({
                    description: a.description,
                    status: a.status,
                })),
                d3_rootCauses: report.d3_rootCause?.map(rc => ({
                    description: rc.description,
                    category: rc.category,
                    verified: rc.verified,
                })),
                d4_corrective: report.d4_correctiveAction?.map(a => ({
                    description: a.description,
                    owner: a.responsibilityName || 'TBD',
                    status: a.status,
                })),
                d5_validation: report.d5_validation?.validationResults,
                d6_preventive: report.d6_preventive?.map(a => ({
                    description: a.description,
                    status: a.status,
                })),
                d7_recognition: report.d7_recognition?.teamAccomplishments,
                d8_closure: report.d8_closure?.closureSummary,
            });
        } catch (error) {
            console.error('Error exporting PDF:', error);
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="mb-6">
            <Link to="/8d" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4">
                <ArrowLeft className="w-4 h-4" />
                Back to 8D reports
            </Link>

            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">
                        {id ? `Edit 8D Report${report.eightDNumber ? `: ${report.eightDNumber}` : ''}` : 'New 8D Report'}
                    </h1>
                    <p className="text-gray-600 mt-1 flex items-center gap-2">
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full font-bold">AIAG VDA Compliant</span>
                        8 Disciplines Problem Solving Method
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={handleExportPDF}
                        disabled={exporting}
                        className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        {exporting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <FileText className="w-4 h-4" />
                        )}
                        {exporting ? 'Exporting...' : 'Export PDF'}
                    </button>

                    <button
                        onClick={() => handleSave()}
                        disabled={saving || report.status === 'Closed'}
                        className="flex items-center gap-2 px-6 py-2 bg-white text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 font-semibold"
                    >
                        <Save className="w-4 h-4" />
                        {saving ? 'Saving...' : 'Save Draft'}
                    </button>

                    {report.status !== 'Closed' && (
                        <button
                            onClick={() => setShowSignModal(true)}
                            disabled={saving}
                            className="flex items-center gap-2 px-6 py-2 bg-slate-900 text-white rounded-lg hover:bg-black transition-colors disabled:opacity-50 font-bold shadow-lg shadow-gray-200"
                        >
                            <ShieldCheck className="w-4 h-4 text-emerald-400" />
                            Sign & Approve
                        </button>
                    )}
                </div>
            </div>

            <ESignatureModal
                isOpen={showSignModal}
                onClose={() => setShowSignModal(false)}
                onSign={handleSign}
                documentTitle={`8D Report: ${report.title || 'Untitled'}`}
            />
        </div>
    );
};
